/**
 * Spreadsheet helper utilities for sorting and filtering table rows
 * Used across ItemPricingSpreadsheet, ClassificationPricingSpreadsheet, ArchivedItemsSpreadsheet, and ArchivedClassificationSpreadsheet
 */

import { Row } from '../tables/PartnerPricingTable';
import { parseDecimalInput } from './decimalInput';

export type SortDirection = 'asc' | 'desc';

/**
 * Get comparable value from a row based on field name
 */
export const getComparableValue = (row: Row, field: string): any => {
  switch (field) {
    case 'ident':
      return row.ident?.name || "";

    case 'identCode':
      return row.ident?.ident || "";

    case 'classification':
      return `${row.classification?.code || ""} ${row.classification?.name || ""}`.trim();

    case 'classificationCode':
      return row.classification?.code || "";

    case 'partnerCode':
      return row.partnerCode || "";

    case 'partnerName':
      return row.partnerName || "";

    case 'dateFrom':
      return row.dateFrom ? new Date(row.dateFrom) : new Date(0);

    case 'dateTo':
      return row.dateTo ? new Date(row.dateTo) : new Date(0);

    case 'createdAt':
      return row.createdAt ? new Date(row.createdAt) : new Date(0);

    case 'quantityDiscount':
    case 'promotionalDiscount':
    case 'regularDiscount':
    case 'paymentDiscount':
    case 'optimalQuantity':
    case 'optimalPrice':
    case 'minimumQuantity':
    case 'qtyPerPack':
    case 'qtyPerPalette':
    case 'price':
      return parseDecimalInput((row as any)[field]) ?? 0;

    default:
      return (row as any)[field] || "";
  }
};

/**
 * Compare two values for sorting
 */
export const compareValues = (aValue: any, bValue: any, direction: SortDirection): number => {
  // Normalize string values to lowercase for case-insensitive comparison
  if (typeof aValue === 'string' && typeof bValue === 'string') {
    aValue = aValue.toLowerCase();
    bValue = bValue.toLowerCase();
  }

  if (aValue < bValue) return direction === 'asc' ? -1 : 1;
  if (aValue > bValue) return direction === 'asc' ? 1 : -1;
  return 0;
};

/**
 * Sort rows by field and direction
 */
export const sortRows = <T extends Row>(
  rows: T[],
  sortField: string | null,
  sortDirection: SortDirection
): T[] => {
  if (!sortField) return rows;

  return [...rows].sort((a, b) => {
    const aValue = getComparableValue(a, sortField);
    const bValue = getComparableValue(b, sortField);
    return compareValues(aValue, bValue, sortDirection);
  });
};

/**
 * Filter rows by search term across multiple fields
 * Supports ident (items), classification, and partnerCode
 */
export const filterRows = <T extends Row>(
  rows: T[],
  searchTerm: string,
  searchFields: ('ident' | 'classification' | 'partnerCode' | 'partnerName')[]
): T[] => {
  if (!searchTerm) return rows;

  const searchLower = searchTerm.toLowerCase();

  return rows.filter(row => {
    return searchFields.some(field => {
      switch (field) {
        case 'ident':
          return (
            (row.ident?.name || "").toLowerCase().includes(searchLower) ||
            (row.ident?.ident || "").toLowerCase().includes(searchLower)
          );

        case 'classification':
          return (
            (row.classification?.code || "").toLowerCase().includes(searchLower) ||
            (row.classification?.name || "").toLowerCase().includes(searchLower)
          );

        case 'partnerCode':
          return (row.partnerCode || "").toLowerCase().includes(searchLower);

        case 'partnerName':
          return (row.partnerName || "").toLowerCase().includes(searchLower);

        default:
          return false;
      }
    });
  });
};

/**
 * Add originalIndex property to rows for tracking after filtering/sorting.
 * When originalRows is provided, originalIndex reflects position in originalRows (by _id),
 * so edits index into the correct row in the flat rows state even after sort/filter.
 * Falls back to positional index when no _id match is found (new unsaved rows).
 */
export const addOriginalIndex = <T extends Row>(
  rows: T[],
  originalRows?: T[]
): (T & { originalIndex: number })[] => {
  return rows.map((row, index) => {
    let originalIndex = index;
    if (originalRows && row._id) {
      const found = originalRows.findIndex(r => r._id === row._id);
      if (found !== -1) originalIndex = found;
    }
    return { ...row, originalIndex };
  });
};
